import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const faqs = [
  {
    category: "Ordering",
    items: [
      {
        q: "How do I place an order?",
        a: "All KIRAQ orders are placed through WhatsApp. Simply click the 'Order on WhatsApp' button on any product page. Our team will confirm availability, share payment details, and arrange delivery."
      },
      {
        q: "How do I know my order is confirmed?",
        a: "Your order is confirmed only after full payment is received. Once payment is completed, you will receive a WhatsApp confirmation with your order summary and shipping details."
      },
    ]
  },

  {
    category: "Materials & Care",
    items: [
      {
        q: "What materials do you use?",
        a: "All KIRAQ pieces are crafted from premium anti-tarnish stainless steel with a gold finish. Our jewellery is designed for durability, everyday elegance, and long-lasting shine."
      },
      {
        q: "Will the gold finish tarnish or fade?",
        a: "Our anti-tarnish stainless steel base significantly extends the life of the gold finish. To maximise longevity, avoid water, perfumes, lotions, sweat, and harsh chemicals."
      },
      {
        q: "How do I care for my KIRAQ jewellery?",
        a: "Store pieces individually in the provided KIRAQ box. Clean gently with a soft dry cloth and avoid exposure to moisture and chemicals for best longevity."
      },
    ]
  },

  {
    category: "Shipping & Packaging",
    items: [
      {
        q: "What does the packaging look like?",
        a: "Every KIRAQ order arrives in our signature packaging — a navy-golden gift box with surprise gift, and a thank-you card."
      },
      {
        q: "How long does shipping take?",
        a: "Orders are dispatched within 2–3 business days of payment confirmation. Delivery across India usually takes 3–5 business days after dispatch."
      },
      {
        q: "Is shipping free?",
        a: "Yes — free shipping is available on eligible orders above ₹499. Shipping charges for smaller orders are communicated during order confirmation."
      },
      {
        q: "Do you ship internationally?",
        a: "Currently, KIRAQ ships within India only. International shipping may become available in the future."
      },
    ]
  },

  {
    category: "Returns & Refunds",
    items: [
      {
        q: "What is your return policy?",
        a: "We offer a 7-day return window from the date of delivery. Items must be unworn, unaltered, and returned in original packaging."
      },
      {
        q: "How do I initiate a return?",
        a: "Contact us via WhatsApp within 7 days of receiving your order. Share your order number and reason for return, and our team will guide you through the process."
      },
      {
        q: "Who pays for return shipping?",
        a: "Return shipping costs are the customer's responsibility unless the item was received damaged, defective, or incorrect."
      },
      {
        q: "When will I receive my refund?",
        a: "Refunds are processed within 5–7 business days after the returned item is received and inspected."
      },
    ]
  },

  {
    category: "Availability",
    items: [
      {
        q: "Why do some pieces say 'Only 1 left'?",
        a: "KIRAQ operates on a limited-edition model. Many pieces are produced in small quantities and are not restocked once sold out."
      },
      {
        q: "Do you offer custom designs?",
        a: "Custom enquiries may be accepted on a case-by-case basis. Please contact us via WhatsApp to discuss availability and timelines."
      },
    ]
  }
];

export default function FAQPage() {
  return (
    <main
      data-testid="faq-page"
      className="pt-20 min-h-screen bg-[#F6F3EE] scroll-smooth"
    >
      <section className="py-16 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="text-center mb-14">
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-6">
              Support
            </p>

            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1E1C1A] mb-5">
              Frequently Asked Questions
            </h1>

            <p className="text-sm font-light leading-relaxed text-[#7A7369] max-w-xl mx-auto">
              Everything you need to know about KIRAQ — our materials,
              ordering process, shipping, and returns.
            </p>
          </div>

          {/* Quick Nav */}
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {faqs.map((section) => (
              <a
                key={section.category}
                href={`#faq-${section.category
                  .toLowerCase()
                  .replace(/ &/g, '')
                  .replace(/ /g, '-')}`}
                className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-[#DDD7CE] text-[#7A7369] hover:border-[#C6A978] hover:text-[#3A3632] transition-colors"
              >
                {section.category}
              </a>
            ))}
          </div>

          {/* FAQ Sections */}
          <div className="space-y-10">
            {faqs.map((section, si) => (
              <div
                key={si}
                id={`faq-${section.category
                  .toLowerCase()
                  .replace(/ &/g, '')
                  .replace(/ /g, '-')}`}
              >
                <h2 className="font-['Cormorant_Garamond'] text-lg font-medium text-[#3A3632] mb-3 pb-2 border-b border-[#DDD7CE] flex items-center gap-3">
                  <span className="text-[#C6A978] text-sm font-normal">
                    {String(si + 1).padStart(2, '0')}
                  </span>
                  {section.category}
                </h2>

                <Accordion type="single" collapsible className="space-y-1.5">
                  {section.items.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`${si}-${i}`}
                      data-testid={`faq-item-${si}-${i}`}
                      className="border border-[#DDD7CE]/60 bg-[#FAFAF8] px-4 sm:px-5"
                    >
                      <AccordionTrigger className="text-left font-['Cormorant_Garamond'] text-base font-medium text-[#1E1C1A] hover:text-[#C6A978] py-4 hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>

                      <AccordionContent className="text-sm font-light leading-7 text-[#7A7369] pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="mt-14 text-center p-8 bg-[#E8E1D8]">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-[#1E1C1A] mb-2">
              Still have questions?
            </h3>

            <p className="text-sm font-light text-[#7A7369] mb-5">
              Our team typically responds within a few hours on WhatsApp.
            </p>

            <a
              href="https://wa.me/919780411102"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-[0.18em] hover:bg-[#1ea855] transition-colors"
            >
              Chat with Us on WhatsApp
            </a>
          </div>

        </div>
      </section>
    </main>
  );
}